import ErrorToast from "../../components/common/toasts/ErrorToast";
import React, { useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import useOrderEditor from "./hooks/useOrderEditor";
import {
  OrderStatus,
  isProcessing,
  eventLabels,
  statusLabels,
  processingDescriptions,
} from "./constants";
import ProgressBar from "../../components/common/ProgressBar";
import OrderActions from "./components/OrderActions";
import DocumentContents from "./components/DocumentContents";
import ReviewForm from "./ReviewForm";
import PremiumLayout from "./components/PremiumLayout";
import { Status } from "./components/Fields";
import { premiumPaths } from "./navigation";
import OrderSteps, {
  getOrderStep,
  orderStepPaths,
} from "./components/OrderSteps";
import PremiumNotice from "./components/PremiumNotice";
import IssueActions from "./components/IssueActions";

export default function OrderDetail({ step: requestedStep }) {
  const { orderId } = useParams();
  const { accessToken } = useAuth();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const {
    order,
    draft,
    dirty,
    busy,
    error,
    openDocument,
    refresh,
    preview,
    approve,
    retry,
    save,
    onChange,
  } = useOrderEditor(orderId, accessToken);
  const step = requestedStep || getOrderStep(order?.status);
  useEffect(() => {
    if (
      requestedStep === 4 &&
      order?.status === OrderStatus.REVIEW &&
      order.previewRevision !== order.revision
    )
      navigate(premiumPaths.preview(orderId), { replace: true });
  }, [requestedStep, order, orderId, navigate]);
  const loaded = Boolean(order);
  useEffect(() => {
    if (loaded && hash.startsWith("#premium-")) {
      document.getElementById(hash.slice(1))?.scrollIntoView({
        block: hash === "#premium-recipients" ? "start" : "center",
      });
    }
  }, [loaded, hash]);
  const showSource = (sources, target) => {
    const available = sources.filter((entry) =>
      order.documents.some((doc) => doc.id === entry.documentId),
    );
    const index = Math.max(
      0,
      available.findIndex((entry) => entry.regions?.length),
    );
    if (available[index])
      openDocument(
        premiumPaths.source(orderId, available[index].documentId, {
          ...target,
          source: index,
        }),
      );
  };
  const documentLabels = {
    ...Object.fromEntries(
      (order?.plugin?.requirements.documents || []).map((item) => [
        item.kind,
        item.label,
      ]),
    ),
    ...order?.plugin?.review.pageLabels,
  };
  const companyName = order?.config.exporter.split("\n")[0];
  return (
    <PremiumLayout
      title={
        requestedStep === 1
          ? "Dokumenty źródłowe"
          : step === 4
            ? "Wystaw i pobierz świadectwo"
            : order?.status === OrderStatus.REVIEW
              ? "Sprawdź świadectwo"
              : "Świadectwo Premium"
      }
      subtitle={companyName}
      actions={
        <Link
          className="btn btn-outline-primary"
          to={
            order
              ? premiumPaths.orders(order.organizationId)
              : premiumPaths.home
          }
        >
          Lista świadectw
        </Link>
      }
    >
      {order && (
        <div className="premium-order-meta">
          <div>
            {order.status !== OrderStatus.ISSUED && (
              <strong>
                {order.draft.invoiceNumbers
                  ? `Faktura: ${order.draft.invoiceNumbers}`
                  : "Dokumenty jednej dostawy"}
              </strong>
            )}
          </div>
          <Status status={order.status} />
        </div>
      )}
      <ErrorToast message={error || order?.error} />
      {!order && !error && <p role="status">Ładowanie…</p>}
      {order && (
        <>
          <OrderSteps
            step={step}
            paths={orderStepPaths(order, { dirty })}
            busy={busy}
            onNavigate={(nextStep, path) => openDocument(path, nextStep === 3)}
          />
          {step !== 4 && (
            <section className="card premium-panel">
              <h2>Dokumenty źródłowe</h2>
              <ul className="premium-files">
                {order.documents.map((doc) => (
                  <li key={doc.id}>
                    <span>
                      {doc.name}{" "}
                      <small>· {documentLabels[doc.kind] || doc.kind}</small>
                      <DocumentContents
                        pages={doc.pages}
                        labels={documentLabels}
                      />
                    </span>
                    <button
                      className="btn btn-outline-primary"
                      disabled={busy}
                      onClick={() =>
                        openDocument(
                          premiumPaths.document(orderId, doc.id, step === 1),
                        )
                      }
                    >
                      Otwórz dokument
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {step === 1 && (
            <div className="card premium-panel">
              <p>
                Dokumenty zostały dodane do tego zlecenia. Możesz je otworzyć
                lub przejść do kolejnego etapu.
              </p>
              <div className="premium-form-actions">
                <button
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => openDocument(premiumPaths.order(orderId))}
                >
                  {getOrderStep(order.status) === 4
                    ? "Przejdź do wystawienia i pobrania"
                    : "Dalej: sprawdź dane"}
                </button>
              </div>
            </div>
          )}
          {step !== 1 && isProcessing(order.status) && (
            <div className="card premium-panel" role="status">
              <div className="premium-toolbar">
                <h2>Przetwarzamy zlecenie</h2>
                <button
                  type="button"
                  onClick={refresh}
                  disabled={busy}
                  className="btn btn-outline-secondary btn-sm premium-refresh"
                  aria-label="Odśwież status"
                  title="Odśwież status"
                >
                  <i className="bi bi-arrow-clockwise" aria-hidden="true" />
                </button>
              </div>
              <ProgressBar
                label={statusLabels[order.status]}
                description={processingDescriptions[order.status]}
              />
              <p>
                Możesz wrócić do listy. Nie przerwie to procesu{" "}
                {order.status === OrderStatus.QUEUED
                  ? "odczytu dokumentów."
                  : "wystawiania świadectwa."}
              </p>
            </div>
          )}
          {step !== 1 && order.status.endsWith("failed") && (
            <button
              className="btn btn-primary premium-space-after"
              disabled={busy}
              onClick={retry}
            >
              Ponów{" "}
              {order.status === OrderStatus.READ_FAILED
                ? "odczyt"
                : "wystawienie tego samego świadectwa"}
            </button>
          )}
          {step === 4 && order.status === OrderStatus.ISSUED && (
            <section className="card premium-panel">
              <h2>Świadectwo jest gotowe</h2>
              <p>Zatwierdzone dane są zapisane w historii zlecenia.</p>
              <button
                className="btn btn-primary"
                disabled={busy}
                onClick={() => openDocument(premiumPaths.certificate(orderId))}
              >
                Otwórz i pobierz świadectwo
              </button>
            </section>
          )}
          {step === 4 &&
            order.status === OrderStatus.REVIEW &&
            order.previewRevision === order.revision && (
              <IssueActions order={order} ready busy={busy} approve={approve} />
            )}
          {step === 2 && order.status === OrderStatus.REVIEW && draft && (
            <>
              <ReviewForm
                draft={draft}
                config={order.config}
                options={order.plugin?.review}
                dictionaryChoices={order.dictionaryChoices}
                dictionaryAccess={{
                  token: accessToken,
                  organizationId: order.organizationId,
                }}
                documents={order.documents}
                disabled={busy}
                onChange={onChange}
                onShowSource={showSource}
              />
              {!dirty && order.warnings.length > 0 && (
                <PremiumNotice warning>
                  <strong>Warto uzupełnić dane:</strong>
                  <p>
                    Poniższe uwagi nie blokują wystawienia. Możesz poprawić dane
                    albo wystawić świadectwo z brakami widocznymi w podglądzie.
                  </p>
                  <ul>
                    {order.warnings.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </PremiumNotice>
              )}
              <OrderActions
                dirty={dirty}
                busy={busy}
                save={save}
                preview={preview}
              />
            </>
          )}
          <details className="card premium-panel">
            <summary>Historia zlecenia</summary>
            <ol>
              {order.events.map((event, index) => (
                <li key={index}>
                  {new Date(event.at).toLocaleString("pl-PL")} —{" "}
                  {eventLabels[event.kind] || event.kind}
                </li>
              ))}
            </ol>
          </details>
        </>
      )}
    </PremiumLayout>
  );
}
